import React, { useEffect, useContext, useState, useCallback } from "react";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { MdDelete } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import moment from "moment";

// Contexts
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import { UserContext } from "../../../../../contexts/User";

// Functions & Components
import { BASE_URL } from "../../../../../BaseUrl";
import { _t, getCookie } from "../../../../../functions/Functions";
import { ToastSuccess, ToastError } from "../../../../../functions/ToastShow";
import { useCustomHooks } from "../../../../../functions/Hooks";
import DataTable from "../../../../common/DataTable";
import DebouncedInput from "../../../../../components/DebouncedInput";
import ManageSidebar from "../ManageSidebar";

const PeelingProcessList = () => {
  const { t } = useTranslation();
  const { roundOffStock } = useCustomHooks();

  const {
    branchForSearch,
    branchForListSearch,
    loading,
    setLoading,
    dataPaginating,
    setDataPaginating,
  } = useContext(RestaurantContext);

  const { authUserInfo } = useContext(UserContext);

  const [btnDisable, setBtnDisable] = useState("");
  const [peelingList, setPeelingList] = useState({
    data: [],
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0,
    from: 0,
    to: 0,
  });

  const [allRecordsForSearch, setAllRecordsForSearch] = useState([]);
  const [searchedItems, setSearchedItems] = useState({
    list: null,
    searched: false,
  });

  const [filterBranch, setFilterBranch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const fetchPeelingList = useCallback(
    async (page = 1, search = "", branchId = "") => {
      setDataPaginating(true);
      try {
        let url = `${BASE_URL}/settings/peeling?page=${page}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (branchId) url += `&branch_id=${branchId}`;

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${getCookie()}` },
        });

        if (res.data) {
          // If response is [paginatedObject, allForSearchList]
          if (Array.isArray(res.data)) {
            if (res.data[0] && res.data[0].data) {
              setPeelingList(res.data[0]);
              setAllRecordsForSearch(res.data[1] || res.data[0].data);
            } else {
              setPeelingList({
                data: res.data,
                current_page: 1,
                last_page: 1,
                per_page: res.data.length,
                total: res.data.length,
                from: 1,
                to: res.data.length,
              });
              setAllRecordsForSearch(res.data);
            }
          } else if (res.data.data && Array.isArray(res.data.data)) {
            setPeelingList(res.data);
            if (!allRecordsForSearch.length) {
              setAllRecordsForSearch(res.data.data);
            }
          }
        }
      } catch (error) {
        console.log("Error fetching peeling process:", error);
      } finally {
        setDataPaginating(false);
        setLoading(false);
      }
    },
    [allRecordsForSearch.length, setDataPaginating, setLoading]
  );

  useEffect(() => {
    fetchPeelingList(1, searchTerm, filterBranch);
    setSearchedItems((prev) => ({
      ...prev,
      searched: searchTerm.length > 0 || filterBranch !== "",
    }));
  }, [filterBranch, searchTerm, fetchPeelingList]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (!value && !filterBranch) {
      setSearchedItems({ list: null, searched: false });
    } else if (allRecordsForSearch && allRecordsForSearch.length > 0) {
      const lower = value.toLowerCase();
      const filtered = allRecordsForSearch.filter((item) => {
        const doc = (item.document_identity || item.document_no || "").toString().toLowerCase();
        const raw = (item.item_name || item.item?.name || item.ingredient?.name || "").toLowerCase();
        const branch = (item.branch?.name || item.branch_name || (branchForListSearch && branchForListSearch[item.branch_id]?.name) || "").toLowerCase();
        const matchSearch = doc.includes(lower) || raw.includes(lower) || branch.includes(lower);
        const matchBranch = filterBranch ? String(item.branch_id || item.branch?.id) === String(filterBranch) : true;
        return matchSearch && matchBranch;
      });
      setSearchedItems({ list: filtered, searched: true });
    }
  };

  const handleFetchData = (page) => {
    fetchPeelingList(page, searchTerm, filterBranch);
  };

  const getListData = useCallback(() => {
    if (searchedItems.searched && searchedItems.list !== null) {
      return searchedItems.list;
    }
    return peelingList?.data || [];
  }, [searchedItems, peelingList]);

  const onClearSearch = () => {
    setSearchTerm("");
    setFilterBranch("");
    setSearchedItems({ list: null, searched: false });
  };

  const handleDeleteConfirmation = (id) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body">
            <h1>{_t(t("Are you sure?"))}</h1>
            <p className="text-center">
              {_t(t("You want to delete this peeling process record?"))}
            </p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleDeletePeeling(id);
                  onClose();
                }}
              >
                {_t(t("Yes, delete it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const handleDeletePeeling = async (id) => {

    setDataPaginating(true);
    const url = `${BASE_URL}/settings/peeling/${id}`;
    try {
      await axios.delete(url, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      fetchPeelingList(1, searchTerm, filterBranch);
      ToastSuccess({
        title: `${_t(t("Peeling process record has been deleted"))}`,
      });
    } catch (error) {
      console.log("Delete error:", error);
      ToastError({ title: `${_t(t("Please try again"))}` });
    } finally {
      setDataPaginating(false);
    }
  };

  const handlePostApi = async (item) => {
    setDataPaginating(true);
    setBtnDisable(item.id);
    const isCurrentlyPosted = Number(item?.is_post) === 1;
    const url = `${BASE_URL}/settings/mark-is-post-peeling`;
    const payload = {
      id: item.id,
      is_post: isCurrentlyPosted ? 0 : 1,
    };

    try {
      const res = await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      });
      if (res.data) {
        toast.success(
          `${_t(
            t(
              isCurrentlyPosted
                ? "Peeling process has been unpost"
                : "Peeling process has been post"
            )
          )}`,
          {
            position: "bottom-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            className: "text-center toast-notification",
          }
        );

        if (Array.isArray(res.data) && res.data[0]) {
          if (res.data[0].data) {
            setPeelingList(res.data[0]);
            setAllRecordsForSearch(res.data[1] || res.data[0].data);
            if (searchedItems.searched) {
              setSearchedItems((prev) => ({
                ...prev,
                list: res.data[1] || res.data[0].data,
              }));
            }
          } else {
            setPeelingList({
              data: res.data,
              current_page: 1,
              last_page: 1,
              per_page: res.data.length,
              total: res.data.length,
              from: 1,
              to: res.data.length,
            });
            setAllRecordsForSearch(res.data);
          }
        } else {
          fetchPeelingList(peelingList?.current_page || 1, searchTerm, filterBranch);
        }
      }
      setDataPaginating(false);
      setBtnDisable("");
    } catch (err) {
      setDataPaginating(false);
      setBtnDisable("");
      if (err.response?.data?.status === "error" || err.response?.data?.message) {
        toast.error(
          `${_t(t(err.response?.data?.message || "Please try again"))}`,
          {
            position: "bottom-center",
            autoClose: 10000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            className: "text-center toast-notification",
          }
        );
        return;
      }
      toast.error(`${_t(t("Please try again"))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    }
  };

  // unpost confirmation modal
  const handleUnPostConfirmation = (item) => {
    confirmAlert({
      customUI: ({ onClose }) => {
        return (
          <div className="card card-body px-5">
            <h3>{_t(t("Are you sure?"))}</h3>
            <p className="text-center">{_t(t("You want to un post this?"))}</p>
            <div className="d-flex justify-content-center">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handlePostApi(item);
                  onClose();
                }}
              >
                {_t(t("Yes, unpost it!"))}
              </button>
              <button className="btn btn-success ml-2 px-3" onClick={onClose}>
                {_t(t("No"))}
              </button>
            </div>
          </div>
        );
      },
    });
  };

  const COLUMNS = [
    {
      label: "S/L",
      render: (item, i) =>
        searchedItems.searched
          ? i + 1
          : i +
            1 +
            ((peelingList?.current_page || 1) - 1) *
              (peelingList?.per_page || 10),
    },
    {
      label: "Document No",
      render: (item) => (
        <span className="font-weight-bold">
          {item.document_identity || (item.document_no ? `PL-${item.document_no}` : "-")}
          {Number(item?.is_post) === 1 && (
            <span className="badge badge-success ml-2 sm-text">
              {_t(t("Posted"))}
            </span>
          )}
        </span>
      ),
    },
    {
      label: "Date",
      render: (item) =>
        item.document_date || item.date
          ? moment(item.document_date || item.date).format("DD-MM-YYYY")
          : "-",
    },
    {
      label: "Branch",
      render: (item) =>
        item.branch?.name ||
        item.branch_name ||
        (branchForListSearch && branchForListSearch[item.branch_id]?.name) ||
        "-",
    },
    {
      label: "Ingredient Item",
      render: (item) => (
        <div className="text-wrap font-weight-500" style={{ maxWidth: "250px" }}>
          {item.item_name || item.item?.name || item.ingredient?.name || "-"}
        </div>
      ),
    },
    {
      label: "Unit",
      render: (item) => (
        <span className="badge badge-secondary sm-text">
          {item.unit_name || item.unit?.name || (item.unit_id ? `Unit ${item.unit_id}` : "-")}
        </span>
      ),
    },
    {
      label: "Before Peeling",
      render: (item) => {
        const val = item.before_peeling_weight ?? (item.qty ?? item.before_peeling);
        return (
          <div className="font-weight-500 text-center">
            {roundOffStock ? roundOffStock(val) : val}
          </div>
        );
      },
    },
    {
      label: "After Peeling",
      render: (item) => {
        const val = item.after_peeling_weight ?? item.after_peeling;
        return (
          <div className="font-weight-bold text-success text-center">
            {roundOffStock ? roundOffStock(val) : val}
          </div>
        );
      },
    },
    {
      label: "Wastage",
      render: (item) => {
        const val = item.wastage_qty ?? item.wastage;
        return (
          <div className="font-weight-bold text-danger text-center">
            {roundOffStock ? roundOffStock(val) : val}
          </div>
        );
      },
    },
    {
      label: "Remarks",
      render: (item) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: "160px" }}
          title={item.remarks || ""}
        >
          {item.remarks || "-"}
        </span>
      ),
    },
    {
      label: "Action",
      render: (item) => {
        const isPosted = Number(item?.is_post) === 1;
        return (
          <div className="d-flex justify-content-center">
            {!isPosted && (
              <>
                <NavLink
                  title="Edit"
                  className="btn btn-primary ml-2 rounded-sm del-btn"
                  to={`/dashboard/manage/stock/peeling-process-edit/${item.id}`}
                  style={{ height: "1.7rem", width: "1.7rem" }}
                >
                  <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
                </NavLink>
                <button
                  title="Delete"
                  className="btn btn-danger ml-2 rounded-sm del-btn"
                  onClick={() => handleDeleteConfirmation(item.id)}
                >
                  <MdDelete className="del-icon" />
                </button>
              </>
            )}
            {!isPosted ? (
              <button
                className="btn btn-dark ml-2 rounded-sm del-btn"
                title="Post Peeling"
                style={{ padding: "1px 0px 0px 1px" }}
                disabled={loading || dataPaginating || btnDisable === item.id}
                onClick={() => handlePostApi(item)}
              >
                <i className="fa fa-check-square-o" aria-hidden="true"></i>
              </button>
            ) : null}
            {authUserInfo?.details?.user_type === "superAdmin" && isPosted ? (
              <button
                className="btn btn-warning ml-2 rounded-sm del-btn"
                title="Unpost Peeling"
                style={{ padding: "1px 0px 0px 1px" }}
                disabled={loading || dataPaginating || btnDisable === item.id}
                onClick={() => handleUnPostConfirmation(item)}
              >
                <i className="fa fa-check-square-o" aria-hidden="true"></i>
              </button>
            ) : null}
          </div>
        );
      },
    },
  ];

  const branches =
    authUserInfo?.details?.user_type === "manager" ||
    authUserInfo?.details?.user_type === "staff"
      ? branchForListSearch && authUserInfo?.details?.branch_id
        ? [branchForListSearch[authUserInfo.details.branch_id]].filter(Boolean)
        : []
      : branchForSearch || [];

  return (
    <>
      <Helmet>
        <title>{_t(t("Peeling Process"))}</title>
      </Helmet>

      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>

            {/* Right Contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="row mx-3 gx-2 align-items-center t-pt-15 t-pb-15">
                  <div className="col-md-5 col-lg-4 t-mb-15 mb-md-0">
                    <ul className="t-list fk-breadcrumb">
                      <li className="fk-breadcrumb__list">
                        <span className="t-link fk-breadcrumb__link text-capitalize">
                          {!searchedItems.searched
                            ? _t(t("Peeling Process List"))
                            : _t(t("Search Result"))}
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div className="col-md-7 col-lg-8">
                    <div className="row gx-3 align-items-center justify-content-end">

                      <div className="col-md-5 t-mb-15 mb-md-0">
                        <div className="input-group">
                          <div className="form-file">
                            <DebouncedInput
                              value={searchTerm}
                              type="text"
                              className="form-control border-0 form-control--light-1 rounded-left"
                              placeholder={_t(t("Search document or item..."))}
                              onChange={handleSearch}
                              disabled={loading || dataPaginating}
                              debounce={500}
                            />
                          </div>
                          <button
                            className="btn btn-primary rounded-right"
                            type="button"
                          >
                            <i className="fa fa-search" aria-hidden="true"></i>
                          </button>
                        </div>
                      </div>

                      <div className="col-md-3 text-md-right">
                        <NavLink
                          to="/dashboard/manage/stock/peeling-process-add"
                          className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                        >
                          {_t(t("add new"))}
                        </NavLink>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <DataTable
                loading={loading || dataPaginating}
                data={getListData()}
                columns={COLUMNS}
                emptyText="No peeling process records available"
                pagination={{
                  isPageLoading: loading || dataPaginating,
                  isPagination: true,
                  pageCount: peelingList?.last_page || 1,
                  currentPage: peelingList?.current_page || 1,
                  from: peelingList?.from || 0,
                  to: peelingList?.to || 0,
                  total: peelingList?.total || 0,
                }}
                handleFetchData={handleFetchData}
                searchPayload={{
                  isSearched: searchedItems.searched,
                  onClearSearch,
                }}
              />
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default PeelingProcessList;
